import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { Camera, Table2, FileText, ArrowLeft, Building2, Upload, Settings, Package, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CatalogWizardModal from '@/components/CatalogWizardModal';

export const ContratoDetailPage = () => {
  const navigate = useNavigate();
  const { selectedProject, refreshProjects } = useApp();
  const { user } = useAuth();
  const [showCatalogUpload, setShowCatalogUpload] = useState(false);
  const { setCaptureDestino } = useApp();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  const handleBack = () => {
    // Super Admin regresa al Admin Dashboard; residentes/contratistas a su lista de contratos
    navigate(user?.role === 'super_admin' ? '/admin' : '/contratos');
  };

  // Residente/contratista: captura sin destino (comportamiento actual, alimenta ambos flujos)
  const handleCapturar = () => {
    setCaptureDestino(null);
    navigate('/catalogo');
  };

  // Super Admin: elige a dónde se enruta la figura antes de entrar al MISMO flujo de captura
  const handleCapturarPara = (destino) => {
    setCaptureDestino(destino);
    navigate('/catalogo');
  };

  const handleSabana = () => {
    navigate('/sabana');
  };

  const handleGeneradores = () => {
    navigate('/generadores');
  };

  const handleOportunidades = () => {
    navigate('/oportunidades');
  };

  if (!selectedProject) {
    navigate('/contratos');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="contrato-detail-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              data-testid="back-to-contratos"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-[#E2EAF4]">{selectedProject.name}</h1>
              <p className="text-[#5A7A9A] text-sm">Detalle del Contrato</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-sm text-[#E2EAF4]">{user?.name}</p>
              <p className="text-xs text-[#5A7A9A] capitalize">
                {user?.role === 'inspector' ? 'Residente' : user?.role === 'contractor' ? 'Contratista' : user?.role === 'super_admin' ? 'Super Admin' : user?.role}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - 3 Action Buttons */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-8 h-8 text-[#0066FF]" />
            <h2 className="text-2xl font-bold text-[#E2EAF4]">{selectedProject.name}</h2>
          </div>
          <p className="text-[#5A7A9A]">Selecciona una opción para continuar</p>
        </div>

        <div className="grid gap-6">
          {/* Admin Panel - Only visible to admins */}
          {isAdmin && (
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-6" data-testid="admin-panel">
              <div className="flex items-center gap-3 mb-4">
                <Settings className="w-5 h-5 text-[#00CFFF]" />
                <h3 className="font-bold text-[#00CFFF]">Panel de Administración</h3>
              </div>
              <Button
                onClick={() => setShowCatalogUpload(true)}
                variant="outline"
                className="text-[#00CFFF] border-[#1E3550] hover:bg-[#0A1422] hover:border-[#00CFFF]"
                data-testid="btn-upload-catalog"
              >
                <Upload className="w-4 h-4 mr-2" />
                Cargar Catálogo de Conceptos
              </Button>
            </div>
          )}

          {/* CAPTURAR — Super Admin ve dos entradas al MISMO flujo; residente/contratista una sola */}
          {isSuperAdmin ? (
            <>
              <button
                onClick={() => handleCapturarPara('presupuesto')}
                className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#0066FF] transition-all text-left group"
                data-testid="btn-capturar-presupuesto"
              >
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-[#0066FF] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0066FF] mb-1">CAPTURA PARA PRESUPUESTO</h3>
                    <p className="text-[#5A7A9A]">Las medidas alimentan Actualización de Presupuesto</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleCapturarPara('avance')}
                className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#0066FF] transition-all text-left group"
                data-testid="btn-capturar-avance"
              >
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-[#0066FF] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0066FF] mb-1">CAPTURA PARA AVANCE DE OBRA</h3>
                    <p className="text-[#5A7A9A]">Las medidas alimentan Generadores y Sábana</p>
                  </div>
                </div>
              </button>
            </>
          ) : (
            <button
              onClick={handleCapturar}
              className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#0066FF] transition-all text-left group"
              data-testid="btn-capturar"
            >
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-[#0066FF] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Camera className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#0066FF] mb-1">CAPTURAR</h3>
                  <p className="text-[#5A7A9A]">Tomar fotos y registrar mediciones</p>
                </div>
              </div>
            </button>
          )}

          {/* SÁBANA Button */}
          <button
            onClick={handleSabana}
            className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#22C55E] transition-all text-left group"
            data-testid="btn-sabana"
          >
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-[#22C55E] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Table2 className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#22C55E] mb-1">SÁBANA</h3>
                <p className="text-[#5A7A9A]">Ver progreso y acumulados del contrato</p>
              </div>
            </div>
          </button>

          {/* GENERADORES Button */}
          <button
            onClick={handleGeneradores}
            className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#FF6B00] transition-all text-left group"
            data-testid="btn-generadores"
          >
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-[#FF6B00] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#FF6B00] mb-1">GENERADORES</h3>
                <p className="text-[#5A7A9A]">Crear PDFs y cerrar períodos</p>
              </div>
            </div>
          </button>

          {/* PRESUPUESTO Button - Solo Super Admin */}
          {user?.role === 'super_admin' && (
          <button
            onClick={() => navigate('/presupuesto')}
            className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#00CFFF] transition-all text-left group"
            data-testid="btn-presupuesto"
          >
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-[#00CFFF] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Calculator className="w-10 h-10 text-[#060C18]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#00CFFF] mb-1">ACTUALIZACIÓN DE PRESUPUESTO</h3>
                <p className="text-[#5A7A9A]">Cantidades levantadas × PU, en vivo y sin conexión</p>
              </div>
            </div>
          </button>
          )}

          {/* OPORTUNIDADES Button */}
          <button
            onClick={handleOportunidades}
            className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 hover:border-[#00CFFF] transition-all text-left group"
            data-testid="btn-oportunidades"
          >
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-[#00CFFF] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Package className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#00CFFF] mb-1">OPORTUNIDADES</h3>
                <p className="text-[#5A7A9A]">Marketplace de materiales</p>
              </div>
            </div>
          </button>
        </div>
      </main>

      {/* Catalog Wizard Modal - Admin Only */}
      {showCatalogUpload && (
        <CatalogWizardModal
          projectId={selectedProject.id}
          projectName={selectedProject.name}
          onClose={() => setShowCatalogUpload(false)}
          onSuccess={() => {
            refreshProjects?.();
            setShowCatalogUpload(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default ContratoDetailPage;
